import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { useMutation as useMutationNew, graphql } from 'react-relay';
import type { PayloadError } from 'relay-runtime';

import { useFlags } from '@atlaskit/flag';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { fg } from '@atlaskit/platform-feature-flags';
import { functionWithCondition } from '@atlaskit/platform-feature-flags-react';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { type SolutionArchitectHandoffPayload } from '@atlaskit/rovo-triggers/solution-architect-types';
import { type Payload } from '@atlaskit/rovo-triggers/types';
import { token } from '@atlaskit/tokens';
import { StudioWorkspaceAri } from '@atlassian/ari';
import { getStudioPath } from '@atlassian/conversation-assistant-agent/common/utils/atlassian-studio-url';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { useMutation as useMutationOld } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import { postSolutionBuild } from '../../../services';
import type { Build, SolutionBuildResponse } from '../../../types';
import { getWorkspaceIdFromUrl } from '../../../utils';
import { useSolutionPlan } from '../../solution-plan-context';

import type { useHandoffAction_planBuildMutation } from './__generated__/useHandoffAction_planBuildMutation.graphql';
import { messages } from './messages';

type HandoffActionOptions = {
	handoffPayloadDataFactory: (
		solutionBuildResponse: SolutionBuildResponse,
	) => SolutionArchitectHandoffPayload['data'];
	onSuccessCallback?: (solutionBuildResponse: SolutionBuildResponse) => void;
};

type HandoffActionResult = {
	isSubmitting: boolean;
	startHandoff: () => void;
};

const noopMutation = () => ({ dispose: () => {} });
const useMutation = functionWithCondition(
	() => fg('fe_enable_sa_externally'),
	useMutationNew,
	() => [noopMutation, false],
);

const noopMutationOld = async () => undefined;
const useMutationOldGated = functionWithCondition(
	() => !fg('fe_enable_sa_externally'),
	<TPayload, TResponse = void>(...args: Parameters<typeof useMutationOld<TPayload, TResponse>>) => {
		di();
		return useMutationOld(...args);
	},
	() => ({ mutate: noopMutationOld, isSubmitting: false, data: null, error: null }),
);

export const useHandoffAction = (
	build: Build,
	options: HandoffActionOptions,
): HandoffActionResult => {
	di(usePublish);

	const { formatMessage } = useIntl();
	const { showFlag } = useFlags();
	const {
		refetchSolutionPlan,
		workspaceId: workspaceIdNew,
		cloudId,
		renderingTouchpoint,
	} = useSolutionPlan();
	const publish = usePublish('ai-mate');
	const { email } = useProfileController();

	const { solutionId, solutionVersion, number: buildNumber } = build;
	const workspaceIdOld = getWorkspaceIdFromUrl();

	const workspaceId = fg('fe_enable_sa_externally') ? workspaceIdNew : workspaceIdOld;

	const [mutate, isSubmittingNew] = useMutation<useHandoffAction_planBuildMutation>(graphql`
		mutation useHandoffAction_planBuildMutation(
			$input: StudioCreateDraftBuildInput!
			$skip: Boolean!
		) {
			studio_createDraftBuild(input: $input)
				@optIn(to: "StudioCreateDraftBuildPayload")
				@skip(if: $skip) {
				build {
					ari
					buildId
					state
					type
				}
			}
		}
	`);

	const { isSubmitting: isSubmittingOld, mutate: mutateOld } = useMutationOldGated({
		mutationFn: () =>
			postSolutionBuild(workspaceId ?? '', solutionId, solutionVersion, buildNumber),
		onSuccess: (solutionBuildResponse) => {
			const payload: Payload = {
				type: 'solution-architect-handoff',
				source: 'ai-mate',
				data: options.handoffPayloadDataFactory(solutionBuildResponse),
			};

			if (renderingTouchpoint !== 'studio' && fg('fe_enable_sa_externally')) {
				// When action trigger outside of studio, we will open a new tab to the solution build in studio.
				const publishTrigger = btoa(JSON.stringify(payload));

				const path = `/w/${workspaceId}/landing`;

				const url = new URL(getStudioPath(path, email));
				url.searchParams.set('publishTrigger', publishTrigger);
				window.open(url.toString(), '_blank');

				return;
			}

			publish(payload);
			refetchSolutionPlan();

			options.onSuccessCallback?.(solutionBuildResponse);
		},
		onError: (error) => {
			logException({ exception: error, name: 'studio-trigger-solution-build-action-button-error' });
			const backendMessage = error.errorAttributes.rawErrorMessage;
			const shouldShowBackendMessage =
				backendMessage &&
				(error.errorAttributes.statusCode === 429 || error.errorAttributes.statusCode === 403);
			showFlag({
				title: formatMessage(messages.solutionBuildErrorTitle),
				icon: <CrossCircleIcon color={token('color.icon.danger')} label="" />,
				description: shouldShowBackendMessage
					? backendMessage
					: formatMessage(messages.solutionBuildErrorDescription),
				isAutoDismiss: true,
			});
		},
	});

	const triggerError = useCallback(
		(error: Error | PayloadError) => {
			if (!fg('fe_enable_sa_externally')) return;

			logException({
				exception: error,
				name: 'studio-trigger-solution-build-action-button-error',
			});
			showFlag({
				title: formatMessage(messages.solutionBuildErrorTitle),
				icon: <CrossCircleIcon color={token('color.icon.danger')} label="" />,
				description: formatMessage(messages.solutionBuildErrorDescription),
				isAutoDismiss: true,
			});
		},
		[showFlag, formatMessage],
	);

	const startHandoff = useCallback(() => {
		if (!fg('fe_enable_sa_externally')) {
			return mutateOld(undefined);
		}

		const workspaceAri = StudioWorkspaceAri.create({
			workspaceId: workspaceId ?? '',
		}).toString();

		return mutate({
			variables: {
				input: {
					solutionId,
					solutionVersion: Number(solutionVersion),
					buildNumber,
					workspaceId: workspaceAri,
				},
				skip: !workspaceId,
			},
			onError: triggerError,
			onCompleted: (response, errors) => {
				if (errors && errors.length > 0) {
					return triggerError(errors[0]);
				}

				const solutionBuildResponse = response?.studio_createDraftBuild?.build;

				const isBuildResponseValid = (
					data: typeof solutionBuildResponse,
					// @ts-expect-error - FIXME: This error was surfaced after enabling the `typescriptExcludeUndefinedFromNullableUnion`
					// Relay compiler option. Previously, `undefined` was incorrectly included in nullable unions in generated types; this option removes it. The owning team should fix this properly.
				): data is SolutionBuildResponse => !!(data?.state && data?.type);

				if (!isBuildResponseValid(solutionBuildResponse)) {
					return triggerError(new Error('No build information returned from server'));
				}

				const payload: Payload = {
					type: 'solution-architect-handoff',
					source: 'ai-mate',
					data: options.handoffPayloadDataFactory(solutionBuildResponse),
				};

				if (renderingTouchpoint !== 'studio') {
					// When triggered outside of studio, navigate to the
					// solution build in studio. The same-tab navigation path
					// (gated on `sltns-1069-popup-blocked-bug-fix`) avoids the
					// browser's popup blocker, which rejects the new-tab
					// `window.open` call once the click's transient
					// user-activation has expired during the async mutation.
					const publishTrigger = btoa(JSON.stringify(payload));

					const path = `/w/${workspaceId}/landing`;

					let handoffUrl: string;
					const url = new URL(getStudioPath(path, email));
					url.searchParams.set('publishTrigger', publishTrigger);
					url.searchParams.set('siteId', cloudId);
					handoffUrl = url.toString();

					if (fg('sltns-1069-popup-blocked-bug-fix')) {
						window.location.assign(handoffUrl);
					} else {
						window.open(handoffUrl, '_blank');
					}

					return;
				}

				publish(payload);
				refetchSolutionPlan();

				options.onSuccessCallback?.(solutionBuildResponse);
			},
		});
	}, [
		cloudId,
		mutate,
		solutionId,
		solutionVersion,
		buildNumber,
		workspaceId,
		renderingTouchpoint,
		email,
		refetchSolutionPlan,
		options,
		publish,
		triggerError,
		mutateOld,
	]);

	const isSubmitting = fg('fe_enable_sa_externally') ? isSubmittingNew : isSubmittingOld;

	return { isSubmitting, startHandoff };
};
